import React, { useState, useEffect, useRef } from 'react'
import { Button, message, Divider, Input, Dropdown } from 'antd'
import dayjs from 'dayjs'
import CONFIG from '../../../constants/config'
import {
  setFilter,
  fetchPhotos,
  resetFilter,
  setQueryFilter,
  setLastPageCond,
  fetchRecommendSearchKeywords
} from '../../../actions/photo'
import { fetchGroupInfo } from '../../../actions/group'
import sensors, { sensorsRole } from '../../../utils/sensors'
import DatesPicker from '../DatesPickerV2'
import WatermarksPicker from '../WatermarksPicker'
import DepartmentMemberPicker from '../DepartmentMemberPickerV2'
import LocationPicker from '../LocationPicker'
import Keywords from '../Keywords'
import { capturePromiseException } from '../../../utils/sentryUtils'
import actionTypes from '../../../constants/actionTypes'
import { setLoading } from '../../../actions/main'
import { buildFetchPhotosParams } from '@/pages/components/photoLib/photo'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  CalendarOutlined,
  CaretDownOutlined,
  RightOutlined
} from '@ant-design/icons'

const quickPickerItems = [
  {
    text: '今天',
    clickItem: 'today',
    startTime: () => dayjs().startOf('day'),
    endTime: () => dayjs().endOf('day')
  },
  {
    text: '昨天',
    clickItem: 'yesterday',
    startTime: () => dayjs().add(-1, 'day').startOf('day'),
    endTime: () => dayjs().add(-1, 'day').endOf('day')
  },
  {
    text: '过去30天',
    clickItem: 'past30days',
    startTime: () => dayjs().add(-30, 'days').startOf('days'),
    endTime: () => dayjs().add(-1, 'days').endOf('days')
  }
]

const ACCEPTANCE_SEARCHED_KEY = 'acceptanceSearched'

function Filter(props) {
  const {
    photoListRef,
    multipleSelect,
    words,
    startJigsawDisable,
    onStartJigsaw,
    handleSensorOfClickEvent
  } = props

  const dispatch = useAppDispatch()
  const filter = useAppSelector((state) => state.photo.filter)
  const queryFilter = useAppSelector((state) => state.photo.queryFilter)
  const groupId = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const members = useAppSelector((state) => state.group.members)
  const isVip = useAppSelector((state) => state.group.isVip)
  const allWatermarksNum = useAppSelector(
    (state) => state.photo.allWatermarksNum
  )
  const pickerUserCount = useAppSelector(
    (state) => state.organization.pickerOrgUserCount
  )

  const [showDatePickerPane, setShowDatePickerPane] = useState(false)
  const [showMemberPickerModal, setShowMemberPickerModal] = useState(false)
  const [showWatermarkModal, setShowWatermarkModal] = useState(false)
  const [showLocationPickerModal, setShowLocationPickerModal] = useState(false)
  const [onlyDisplayMine, setOnlyDisplayMine] = useState(true)

  const moreFilterDropdown = useRef(null)

  useEffect(() => {
    if (
      myRole === CONFIG.memberRoleTypes.ADMIN ||
      myRole === CONFIG.memberRoleTypes.MASTER_ADMIN
    ) {
      setOnlyDisplayMine(false)
    } else {
      setOnlyDisplayMine(true)
    }
  }, [myRole])

  const handleDatesChange = (startDate, endDate) => {
    setShowDatePickerPane(false)
    if (startDate.isAfter(endDate)) {
      dispatch(setFilter({ ...filter, times: [endDate, startDate] }))
    } else {
      dispatch(setFilter({ ...filter, times: [startDate, endDate] }))
    }
  }

  const handleLocationChange = (location) => {
    setShowLocationPickerModal(false)
    dispatch(setFilter({ ...filter, locations: [location] }))
  }

  const handleMemberChangeV2 = (params) => {
    const { selectedDepartmentUsers, selectedDepartments } = params
    setShowMemberPickerModal(false)
    dispatch(
      setFilter({
        ...filter,
        departments: selectedDepartments,
        departmentUsers: selectedDepartmentUsers
      })
    )
  }

  const handleCancelChecked = () => {
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
    dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })
  }

  // 注意：query 必须由调用方显式传入，不能从 useSelector 的 queryFilter 闭包里读。
  // hooks 闭包捕获 render 时的旧 queryFilter；class 组件 this.props.queryFilter 是
  // 引用每次访问最新值，所以 master 没这个 bug。
  const handleFetchPhotos = (query: any) => {
    handleCancelChecked()

    if (!query) return

    const requestStartTime = new Date().getTime()
    const properties = {
      searchContent: query.searchKeywords,
      fileType:
        query.mediaType === null
          ? 'all'
          : query.mediaType === 0
            ? 'photo'
            : 'video',
      dateOffset: query.times[1].diff(query.times[0], 'days') + 1,
      startTime: query.times[0].format(CONFIG.timeFormat),
      endTime: query.times[1].format(CONFIG.timeFormat),
      memberNum: handleCalcPickedMemberNum(),
      isAllMember: handleCalcPickedMemberNum() === members.length,
      watermarkNum: query.watermarks.length,
      isAllWatermark:
        query.watermarks.length === 0 ||
        query.watermarks.length === allWatermarksNum,
      photoLocation: (query.locations[0] && query.locations[0].name) || '',
      role: sensorsRole(myRole),
      groupID: groupId
    }
    sensors.track('web_workgroup_picspage_click_filter', properties)

    dispatch(fetchPhotos(buildFetchPhotosParams(query as any, groupId, '')))
      .then((result) => {
        sensors.track('web_workgroup_picspage_filter_suc', {
          processTime: new Date().getTime() - requestStartTime,
          firstPageNum: result.payload.total
        })

        photoListRef.current &&
          photoListRef.current.scrollToPosition({
            scrollLeft: 0,
            scrollTop: 0
          })
      })
      .catch(capturePromiseException)
  }

  const handleSubmit = () => {
    setShowDatePickerPane(false)
    setShowMemberPickerModal(false)
    setShowWatermarkModal(false)
    setShowLocationPickerModal(false)
    dispatch(setLastPageCond(''))
    // 直接用本次 click 时拿到的 filter 触发拉取，避免依赖 queryFilter 闭包导致
    // 第一次请求带旧条件、第二次才正确的现象
    dispatch(setQueryFilter(filter)).then(() => handleFetchPhotos(filter))
    if (filter.searchKeywords.includes('验收')) {
      localStorage.setItem(ACCEPTANCE_SEARCHED_KEY, 'true')
    }

    handleSensorOfClickEvent('beginSearch')
  }

  const handleReset = () => {
    dispatch(setLastPageCond(''))
    // resetFilter 把 redux 里 queryFilter 重置回 initialFilter，对应取
    // store 当前值（也可以从 resetFilter 的 promise return 拿，简化用 store）
    dispatch(resetFilter()).then((nextQueryFilter: any) =>
      handleFetchPhotos(nextQueryFilter)
    )
    handleSensorOfClickEvent('reset')
  }

  const handleFilterModals = (type) => {
    if (type === 'time') {
      sensors.track('web_workgroup_filter_time_click', {
        pagePath: window.location.pathname
      })

      handleSensorOfClickEvent('choosePhotoDate')

      setShowDatePickerPane(!showDatePickerPane)
      setShowMemberPickerModal(false)
      setShowWatermarkModal(false)
      setShowLocationPickerModal(false)
    }
    if (type === 'member') {
      handleSensorOfClickEvent('choosePhotoMember')

      setShowMemberPickerModal(!showMemberPickerModal)
      setShowDatePickerPane(false)
      setShowWatermarkModal(false)
      setShowLocationPickerModal(false)
    }
    if (type === 'watermark') {
      handleSensorOfClickEvent('chooseWatermark')

      setShowWatermarkModal(!showWatermarkModal)
      setShowDatePickerPane(false)
      setShowMemberPickerModal(false)
      setShowLocationPickerModal(false)
    }
    if (type === 'location') {
      handleSensorOfClickEvent('chooseLocation')

      setShowLocationPickerModal(!showLocationPickerModal)
      setShowWatermarkModal(false)
      setShowDatePickerPane(false)
      setShowMemberPickerModal(false)
    }
  }

  const handleWatermarksChange = (checkedList) => {
    const ids = checkedList.map((id) => id.split('+'))
    sensors.track('web_workgroup_filter_watermark_page_click_done', {
      watermarkNum: checkedList.length,
      groupWatermarkNum: ids.filter((id) => id[1]).length,
      basicWatermarkNum: ids.filter((id) => id[0] !== '0' && id[1] === '')
        .length,
      noWatermarkNum: ids.filter((id) => id[0] === '0').length
    })
    setShowWatermarkModal(false)
    dispatch(setFilter({ ...filter, watermarks: checkedList }))
  }

  const handleCalcPickedMemberNum = () => {
    const userNumInDepartments = filter.departments
      .map((department) => department.num)
      .reduce((acc, cur) => acc + cur, 0)
    const total = filter.departmentUsers.length + userNumInDepartments
    const hasSelection =
      filter.departmentUsers.length > 0 || filter.departments.length > 0
    if (
      (!hasSelection && total === 0) ||
      total === pickerUserCount ||
      filter.departments.some((department) => department.id === '0')
    ) {
      return pickerUserCount
    } else {
      return total
    }
  }

  const handleCalcMemberPickedValue = () => {
    const pickedMembersNum = handleCalcPickedMemberNum()
    if (pickedMembersNum === pickerUserCount) {
      return '全部成员'
    }
    return `已选${pickedMembersNum}人`
  }

  const handleCalcWatermarkPickedValue = () => {
    return filter.watermarks.length === allWatermarksNum ||
      filter.watermarks.length === 0
      ? `全部(${allWatermarksNum}个)`
      : `${filter.watermarks.length}个水印`
  }

  const handleFilterMoreCount = () => {
    let count = 0
    if (filter.locations.length > 0) {
      count++
    }
    if (filter.mediaType !== null) {
      count++
    }
    return count
  }

  const renderTimePickerArea = () => {
    const startTime = filter.times[0]
    const endTime = filter.times[1]
    const isThisMonth =
      dayjs().startOf('month').isSame(startTime, 'second') &&
      dayjs().endOf('day').isSame(endTime, 'second')
    const isAllPhoto =
      startTime.isSame(dayjs('1970-01-01')) &&
      endTime.isSame(dayjs().endOf('days'))
    const startTimeFormated = dayjs().isSame(startTime, 'year')
      ? startTime.format('M月D日')
      : startTime.format('YY年M月D日')
    const endTimeFormated = dayjs().isSame(endTime, 'year')
      ? endTime.format('M月D日')
      : endTime.format('YY年M月D日')

    return (
      <div className="time-wrapper">
        <p className="label">筛选时间</p>
        <p
          className="timepicker-wrapper"
          onClick={() => handleFilterModals('time')}
        >
          {isThisMonth && <span className="this-month">本月 丨 </span>}
          {isAllPhoto ? (
            <span className="this-month">所有照片</span>
          ) : (
            startTimeFormated + '-' + endTimeFormated
          )}
          <CalendarOutlined />
        </p>
        <p className="quickpicker-wrapper">
          快速筛选{' '}
          {quickPickerItems.map((item, index) => {
            return (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  handleDatesChange(item.startTime(), item.endTime())
                  handleSensorOfClickEvent('quickChooseTime', {
                    clickQuickTimeContent: item.text
                  })
                }}
                key={index}
              >
                {item.text}
              </Button>
            )
          })}
        </p>
        <DatesPicker
          visible={showDatePickerPane}
          dates={filter.times}
          onChange={handleDatesChange}
          onCancel={() => setShowDatePickerPane(false)}
        />
      </div>
    )
  }

  const renderKeywordsArea = () => {
    return (
      <Keywords
        value={filter.searchKeywords}
        onChange={(value: string[]) => {
          dispatch(setFilter({ ...filter, searchKeywords: value }))
        }}
        onSensorOfClickEvent={handleSensorOfClickEvent}
        onSubmit={handleSubmit}
      />
    )
  }

  const filterMoreMenuItems = [
    {
      key: 'location',
      label: (
        <span>
          筛选地点：{filter.locations.length > 0 ? '已选择' : '不限'}
          <RightOutlined />
        </span>
      ),
      onClick: () => handleFilterModals('location')
    },
    {
      key: 'mediaType',
      label: (
        <span>
          {`内容类型：${
            filter.mediaType === null
              ? '全部'
              : filter.mediaType === 0
                ? '仅照片'
                : '仅视频'
          }`}
        </span>
      ),
      children: [
        {
          key: 'all',
          label: '全部类型',
          onClick: () => {
            dispatch(setFilter({ ...filter, mediaType: null }))
            handleSensorOfClickEvent('chooseFileType')
          }
        },
        {
          key: 'photo',
          label: '照片',
          onClick: () => {
            dispatch(setFilter({ ...filter, mediaType: 0 }))
            handleSensorOfClickEvent('chooseFileType')
          }
        },
        {
          key: 'video',
          label: '视频',
          onClick: () => {
            dispatch(setFilter({ ...filter, mediaType: 1 }))
            handleSensorOfClickEvent('chooseFileType')
          }
        }
      ]
    }
  ]

  const filterMoreNum = handleFilterMoreCount()

  return (
    <div className="photo-filter-wrapper">
      <div style={{ display: 'inline-block' }}>
        <div className="time-and-keywords-wrapper">
          {renderTimePickerArea()}
          <Divider
            orientation="vertical"
            style={{ height: '52px', margin: '8px 17px' }}
          />
          {renderKeywordsArea()}
        </div>
        <div className="other-filter-wrapper" ref={moreFilterDropdown}>
          <div
            className="member-filter-dropdown"
            onClick={() => handleFilterModals('member')}
          >
            <span className="label">筛选人员</span>
            <span className="value">{handleCalcMemberPickedValue()}</span>
            <CaretDownOutlined />
          </div>
          <Divider
            orientation="vertical"
            style={{ height: '26px', margin: '0 16px' }}
          />

          <div
            className="member-filter-dropdown"
            onClick={() => handleFilterModals('watermark')}
          >
            <span className="label">筛选水印</span>
            <span className="value">{handleCalcWatermarkPickedValue()}</span>
            <CaretDownOutlined />
          </div>
          <Divider
            orientation="vertical"
            style={{ height: '26px', margin: '0 16px' }}
          />
          <Dropdown
            menu={{ items: filterMoreMenuItems }}
            classNames={{ root: 'filter-more-menu' }}
            getPopupContainer={() =>
              (moreFilterDropdown.current || document.body) as HTMLElement
            }
          >
            <div
              className={`more-filter-dropdown ${
                filterMoreNum && 'more-filtered'
              }`}
            >
              <span className="label">
                更多筛选{filterMoreNum > 0 && `(${filterMoreNum})`}
              </span>
              <CaretDownOutlined />
            </div>
          </Dropdown>

          <DepartmentMemberPicker
            open={showMemberPickerModal}
            filterObj={filter as any}
            onOk={handleMemberChangeV2}
            onCancel={() => setShowMemberPickerModal(false)}
          />
          <WatermarksPicker
            visible={showWatermarkModal}
            onOK={handleWatermarksChange}
            onCancel={() => setShowWatermarkModal(false)}
            checkedList={filter.watermarks}
          />
          <LocationPicker
            visible={showLocationPickerModal}
            onCancel={() => setShowLocationPickerModal(false)}
            groupId={groupId}
            handleCheckLocation={handleLocationChange}
          />
        </div>
        <Button
          size="large"
          style={{ marginLeft: '20px' }}
          onClick={handleReset}
        >
          重置
        </Button>
        <Button
          size="large"
          type="primary"
          ghost
          style={{ width: '145px', marginLeft: '12px' }}
          onClick={handleSubmit}
        >
          筛选
        </Button>
      </div>
      {multipleSelect && (
        <div className="extra-wrapper">
          <Button
            type="primary"
            className="batch-select-btn"
            disabled={startJigsawDisable}
            onClick={onStartJigsaw}
          >
            {words.submitBtnText}
          </Button>
        </div>
      )}
    </div>
  )
}

export default Filter
